import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Edit, Trash2, Eye } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { createPageUrl } from '../../../../utils/index';
import { formatCurrency, creditTypeLabels } from './utils';
import ApplicationStatusBadge from './ApplicationStatusBadge';

interface Application {
  id: string | number;
  application_number?: string;
  submission_number?: string;
  debtor_id?: string | number;
  credit_type?: string;
  requested_amount?: number;
  tenor_months?: number;
  total_score?: number;
  status: string;
}

interface ApplicationRowProps {
  application: Application;
  debtorName: string;
  onEdit?: (id: string | number) => void;
  onDelete?: (id: string | number) => void;
  onView?: (id: string | number) => void;
}

export default function ApplicationRow({
  application,
  debtorName,
  onEdit,
  onDelete,
  onView,
}: ApplicationRowProps) {
  const navigate = useNavigate();
  const appNumber = application.application_number || application.submission_number || '-';
  const appId = application.id;
  const canEdit = application.status === 'draft';

  const handleView = () => {
    if (onView) {
      onView(appId);
    } else {
      // Extract actual ID (remove prefix if exists)
      const actualId =
        typeof appId === 'string' && appId.includes('_') ? appId.split('_')[1] : appId;
      navigate(`/credit/application/detail?id=${actualId}&from=application-list`);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(appId);
    } else {
      navigate(createPageUrl(`CreditSubmissionForm?id=${appId}`));
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(appId);
    }
  };

  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-4 py-3 font-mono text-sm">{appNumber}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <span className="font-semibold text-blue-700">{debtorName?.charAt(0) || 'D'}</span>
          </div>
          <span className="font-medium">{debtorName}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="px-2 py-1 text-xs font-medium rounded-full border border-gray-300 bg-gray-50 text-gray-700">
          {creditTypeLabels[application.credit_type || ''] || application.credit_type || '-'}
        </span>
      </td>
      <td className="px-4 py-3 font-medium">{formatCurrency(application.requested_amount)}</td>
      <td className="px-4 py-3">
        {application.tenor_months ? `${application.tenor_months} bulan` : '-'}
      </td>
      <td className="px-4 py-3">
        {application.total_score !== undefined && application.total_score !== null ? (
          <span
            className={`font-semibold ${
              application.total_score >= 70
                ? 'text-green-600'
                : application.total_score >= 50
                  ? 'text-amber-600'
                  : 'text-red-600'
            }`}
          >
            {application.total_score}
          </span>
        ) : (
          '-'
        )}
      </td>
      <td className="px-4 py-3">
        <ApplicationStatusBadge status={application.status} />
      </td>
      <td className="px-4 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleView}>
              <Eye className="w-4 h-4 mr-2" /> Lihat Detail
            </DropdownMenuItem>
            {canEdit && (
              <>
                <DropdownMenuItem onClick={handleEdit}>
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
                {onDelete && (
                  <DropdownMenuItem className="text-red-600" onClick={handleDelete}>
                    <Trash2 className="w-4 h-4 mr-2" /> Hapus
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}
